import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import { Box, Button, H4, Input } from '@deity-io/portal-ui';
import { cloneDeep } from 'lodash';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
// import { useLocation } from 'react-router-dom';
// import { getParams } from 'utils';
// import { ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import {
  useExecuteStatusCommandMutation,
  useOnManagementCommandLogLineAddedSubscription
} from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';

interface IStatusTabProps {
  isEditable?: boolean;
}

interface IClusterState {
  services: Record<string, Record<string, { uuid: string }>>;
  // packages: Record<string, { uuid: string; instances: Record<string, { uuid: string; pipelines: IPropertyBag }> }>;
  packages: Record<
    string,
    {
      uuid: string;
      pipelines: Record<string, { uuid: string; instances: Record<string, { uuid: string; data: IPropertyBag }> }>;
    }
  >;
}

class ServerStatusEventFactory {
  static create(payload: IServerStatusEvent): any | undefined {
    switch (payload.action) {
      case 'status':
        return {
          instanceId: payload.instanceId,
          instanceType: payload.instanceType,
          message: payload.message
        };
      default:
    }
  }
}
interface IServerStatusEvent {
  uuid: string;
  instanceId?: string | null | undefined;
  instanceType?: string | null | undefined;
  action?: string | null | undefined;
  message: any;
}

interface IServerStatusReportEvent {
  instanceId: string;
  instanceType: string;
  action: 'statusReport';
  message: Record<string, { pipelines: Record<string, any> }>;
}

interface IServerStatusLogEvent {
  instanceId?: string | null | undefined;
  instanceType?: string | null | undefined;
  action: 'log';
  message: any;
}

class ServerStatusEventAggregator {
  static add(inputState: IClusterState, event: IServerStatusEvent): IClusterState | undefined {
    const state = cloneDeep(inputState);
    if (event.action === 'statusReport') {
      const reportEvent = event as IServerStatusReportEvent;
      if (!(reportEvent.instanceType in state.services)) {
        state.services[reportEvent.instanceType] = {};
      }
      if (!(reportEvent.instanceId in state.services[reportEvent.instanceType])) {
        state.services[reportEvent.instanceType][reportEvent.instanceId] = {
          uuid: reportEvent.instanceId
        };
      }

      mapRecord(reportEvent.message, (value, packageKey) => {
        // eslint-disable-next-line no-console
        console.log(packageKey, value);
        if (!(packageKey in state.packages)) {
          state.packages[packageKey] = { uuid: packageKey, pipelines: {} };
        }

        mapRecord(value.pipelines, (pipeline, pipelineKey) => {
          if (!(pipelineKey in state.packages[packageKey].pipelines)) {
            state.packages[packageKey].pipelines[pipelineKey] = { uuid: pipelineKey, instances: {} };
          }
          if (!(reportEvent.instanceId in state.packages[packageKey].pipelines[pipelineKey].instances)) {
            state.packages[packageKey].pipelines[pipelineKey].instances[reportEvent.instanceId] = {
              uuid: reportEvent.instanceId,
              data: pipeline
            };
          }
        });
      });

      return state;
    }
  }
}

/**
 * call status. wait for returning exchange created.
 * when created, start listening for log lines
 * somehow stop listening
 */
export const ServerStatusTab: FC<IStatusTabProps> = ({ isEditable = true }) => {
  // const isEditable = true;
  // const { pathname } = useLocation();
  const inputRef = useRef<any>(null);
  const [clusterState, setClusterState] = useState<IClusterState>({ services: {}, packages: {} });
  const idleTimeOut = useRef<null | NodeJS.Timeout>(null);
  const stopIdleTimer = () => {
    if (idleTimeOut.current) {
      const timer = idleTimeOut.current;
      idleTimeOut.current = null;
      clearTimeout(timer);
    }
  };

  const startIdleTimer = () => {
    idleTimeOut.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('idleTimeOutExpired');
      const newState = { ...componentState };
      delete newState.sessionUuid;
      stopIdleTimer();
      setComponentState(newState);
    }, 60 * 1000);
  };

  // const params: { [key: string]: string } = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [componentState, setComponentState] = useState<{ sessionUuid?: string }>({});
  const [executeCommand, { data: mutationResult, loading: runningStatusMutation }] = useExecuteStatusCommandMutation();
  const {
    data: logData,
    loading: subscriptionLoading,
    error
  } = useOnManagementCommandLogLineAddedSubscription({
    variables: {
      uuid: componentState.sessionUuid || 'undefined',
      exchangeName: mutationResult?.executeStatusCommand.exchangeName || 'undefined',
      queueName: mutationResult?.executeStatusCommand.queueName || 'undefined'
    },
    skip: componentState.sessionUuid === undefined || mutationResult?.executeStatusCommand.exchangeName === undefined,
    onSubscriptionComplete() {
      // eslint-disable-next-line no-console
      console.log('onSubscriptionComplete');
      stopIdleTimer();
    },
    onSubscriptionData: dataHolder => {
      stopIdleTimer();
      try {
        const data = dataHolder.subscriptionData?.data?.managementCommandLogLineAdded;
        if (data) {
          // eslint-disable-next-line no-console
          console.log(data);
          const { action, uuid, ...info } = data;
          const event = ServerStatusEventFactory.create(data);
          if (inputRef.current) {
            inputRef.current.value += JSON.stringify(data.message, null, 2);
          }
          const newState = ServerStatusEventAggregator.add(clusterState, data);
          if (newState) {
            // eslint-disable-next-line no-console
            console.log('newState', newState);
            setClusterState(newState);
          }
        }
      } finally {
        startIdleTimer();
      }
    }
  });
  /**
   * Bring up and tear down the graph component
   */
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('initEffect');

    // Specify how to clean up after this effect:
    return function cleanup() {
      // eslint-disable-next-line no-console
      console.log('cleanup');
      stopIdleTimer();
      setComponentState({});
    };
  }, []);

  if (error) {
    // eslint-disable-next-line no-console
    console.log('SubError', error);
  }

  return (
    <Box>
      <Button
        key="query"
        disabled={!(isEditable && !runningStatusMutation)}
        onClick={() => {
          if (!runningStatusMutation) {
            const myUuid = v4();
            // console.log('myUuid', myUuid);
            setComponentState({ ...componentState, sessionUuid: myUuid });
            executeCommand({ variables: { uuid: myUuid } }).then(result => {
              // console.log('command result', result);
              startIdleTimer();
            });
          }
        }}
      >
        Query Status
      </Button>
      <Button
        key="stop"
        disabled={!isEditable || logData === undefined}
        onClick={() => {
          const newState = { ...componentState };
          delete newState.sessionUuid;
          stopIdleTimer();
          setComponentState(newState);
        }}
      >
        Stop Listening
      </Button>
      {subscriptionLoading ? 'subscriptionLoading' : ''}
      {runningStatusMutation ? 'runningStatusMutation' : ''}

      <Box>
        <H4>services:</H4>
        {mapRecordToArray(clusterState.services, (service, serviceKey) => (
          <Box>
            <Box key={serviceKey}>service : {serviceKey}</Box>
            <Box>
              {mapRecordToArray(service, (instance, instanceKey) => (
                <Box>instance:{instanceKey}</Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Box>
        <H4>packages:</H4>
        {mapRecordToArray(clusterState.packages, (packageData, packageKey) => (
          <Box key={packageKey}>
            <Box key="cdfas">packageKey:{packageKey}</Box>
            <Box>
              {mapRecordToArray(packageData.pipelines, (value, pipelineKey) => (
                <Box key={pipelineKey}>
                  <Box>pipelineKey:{pipelineKey}</Box>
                  {mapRecordToArray(value.instances, (instance, instanceKey) => (
                    <Box>
                      instanceKey:{instanceKey}
                      <pre>{JSON.stringify(instance.data, null, 2)}</pre>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Input type="text" multiline fullWidth inputRef={inputRef} />
    </Box>
  );
};
